/**
 * In-app help (T11-47, design/04 §0 "ตัวช่วยในหน้า"): one short Markdown file per topic and staff
 * language — `src/help/<ja|th|en>/<topic>.md`. The first paragraph under the `# title` is the
 * popover of the "?" next to a field (`<HelpTip topic="…">`); the whole file is the help page
 * `/help/<topic>` ("Read more"). Guides (`guide-*`) are the 30-second walk-throughs of the five
 * main jobs, with screenshots from the e2e run (`public/guides/<guide>/<lang>/<n>.jpg`,
 * `E2E_GUIDES=1 pnpm --filter admin test:e2e e2e/guides.spec.ts`).
 *
 * `node scripts/help-check.mjs` (part of `pnpm lint`) fails when a topic misses a language, a
 * `topic="…"` in the code has no file, or a legal/tax word below has no "?" in the screens.
 */
import type { AdminLocale } from "@/i18n";

export const HELP_TOPICS = [
  "t-number",
  "tax-included",
  "tax-free",
  "export-tax",
  "returns-special",
  "fee-bearer",
  "rank",
  "kobutsu-ledger",
  "identity-check",
  "consigned-receipt",
  "store-credit",
  "bank-transfer",
  "getting-started",
] as const;

export const GUIDES = [
  "guide-add-card",
  "guide-confirm-transfer",
  "guide-ship-order",
  "guide-consignment-intake",
  "guide-kobutsu-export",
] as const;

export type HelpTopic = (typeof HELP_TOPICS)[number];
export type GuideTopic = (typeof GUIDES)[number];
export type AnyTopic = HelpTopic | GuideTopic;

/**
 * Words of the law / tax that must have a "?" wherever staff type them (T11-47 acceptance):
 * topic → where it is used (checked by scripts/help-check.mjs against the source).
 */
export const LEGAL_TOPICS: readonly HelpTopic[] = [
  "t-number",
  "tax-free",
  "returns-special",
  "fee-bearer",
  "rank",
];

/** The "?" of the page header opens the guide of the job done on that page. */
const GUIDE_OF_PATH: ReadonlyArray<readonly [RegExp, GuideTopic]> = [
  [/^\/products\/new$/, "guide-add-card"],
  [/^\/orders\/bank-transfers$/, "guide-confirm-transfer"],
  [/^\/orders\/ship$/, "guide-ship-order"],
  [/^\/orders\/[^/]+$/, "guide-ship-order"],
  [/^\/consignment\/(items|consignors)(\/.*)?$/, "guide-consignment-intake"],
  [/^\/consignment\/kobutsu-ledger$/, "guide-kobutsu-export"],
];

export function guideForPath(pathname: string): GuideTopic | null {
  if (pathname === "/orders/new") {
    return null;
  }
  return GUIDE_OF_PATH.find(([pattern]) => pattern.test(pathname))?.[1] ?? null;
}

export function isTopic(value: string | undefined): value is AnyTopic {
  return (
    value !== undefined &&
    ((HELP_TOPICS as readonly string[]).includes(value) ||
      (GUIDES as readonly string[]).includes(value))
  );
}

const FILES = import.meta.glob<string>("./*/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
});

export interface HelpDoc {
  topic: AnyTopic;
  title: string;
  /** First paragraph — the popover text. */
  summary: string;
  /** Everything after the title (Markdown). */
  body: string;
  /** The language the text is in (English when the staff language misses the file). */
  language: AdminLocale;
}

function parse(topic: AnyTopic, language: AdminLocale, raw: string): HelpDoc {
  const lines = raw.replace(/\r\n/g, "\n").split("\n");
  const titleLine = lines.findIndex((line) => line.startsWith("# "));
  const title = titleLine >= 0 ? (lines[titleLine] ?? "").slice(2).trim() : topic;
  const body = lines
    .slice(titleLine + 1)
    .join("\n")
    .trim();
  const summary = (body.split(/\n\s*\n/)[0] ?? "").replace(/\s*\n\s*/g, " ").trim();
  return { topic, title, summary: stripInline(summary), body, language };
}

/** Plain text of one Markdown line (for the popover and `aria` labels). */
export function stripInline(text: string): string {
  return text
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1");
}

export function helpDoc(topic: AnyTopic, language: string | undefined): HelpDoc | null {
  const wanted: AdminLocale = language === "ja" || language === "th" ? language : "en";
  for (const lang of [wanted, "en"] as const) {
    const raw = FILES[`./${lang}/${topic}.md`];
    if (raw !== undefined) {
      return parse(topic, lang, raw);
    }
  }
  return null;
}

/** Topic files present per language (the Help page list + tests). */
export function availableTopics(language: AdminLocale): AnyTopic[] {
  return [...HELP_TOPICS, ...GUIDES].filter((t) => FILES[`./${language}/${t}.md`] !== undefined);
}
